import React, { useState, useEffect, useCallback } from 'react';
import { LexAIService } from '../services/api';
import { SharePointFilePreview } from '../types/api';

interface FileDetailsProps {
  file: any;
}

const FileDetails: React.FC<FileDetailsProps> = ({ file }) => {
  const [preview, setPreview] = useState<SharePointFilePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageZoom, setImageZoom] = useState(1);

  const isSharePoint = file?.source === 'sharepoint';
  const extension = (file?.extension || file?.name?.split('.').pop() || '').toLowerCase().replace('.', '');

  // Load preview for SharePoint files
  useEffect(() => {
    if (!isSharePoint || !file?.fullPath) return;

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    LexAIService.getSharePointFilePreview(file.fullPath, controller.signal)
      .then(data => {
        if (data) {
          setPreview(data);
        } else {
          setError('Could not load file preview');
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Failed to load preview');
        }
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [isSharePoint, file?.fullPath]);

  const handleOpenInSharePoint = useCallback(() => {
    if (file?.webUrl) {
      window.open(file.webUrl, '_blank', 'noopener,noreferrer');
    }
  }, [file?.webUrl]);

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (date?: string | Date): string => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getFileIcon = (): string => {
    switch (extension) {
      case 'pdf': return 'picture_as_pdf';
      case 'doc': case 'docx': return 'article';
      case 'xls': case 'xlsx': return 'table_chart';
      case 'ppt': case 'pptx': return 'slideshow';
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'webp': case 'bmp': return 'image';
      case 'md': return 'edit_note';
      case 'txt': return 'text_snippet';
      case 'csv': return 'table_rows';
      default: return 'description';
    }
  };

  const getFileColor = (): string => {
    switch (extension) {
      case 'pdf': return '#e53935';
      case 'doc': case 'docx': return '#1976d2';
      case 'xls': case 'xlsx': return '#2e7d32';
      case 'ppt': case 'pptx': return '#e65100';
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'webp': case 'bmp': return '#7b1fa2';
      case 'md': case 'txt': case 'csv': return '#546e7a';
      default: return 'var(--obsidian-text-secondary)';
    }
  };

  if (!file) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100%', color: 'var(--obsidian-text-secondary)'
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }}>description</span>
        <h3 style={{ margin: '0 0 8px', color: 'var(--obsidian-text-primary)' }}>File Not Found</h3>
        <p>No file data available</p>
      </div>
    );
  }

  // Render markdown/text content inline
  const renderTextContent = () => {
    if (!preview?.content) return null;

    const isMarkdown = extension === 'md';

    return (
      <div style={{
        flex: 1, overflow: 'auto', padding: '24px',
        backgroundColor: 'var(--obsidian-bg)',
        fontFamily: isMarkdown ? 'inherit' : 'monospace',
        fontSize: '14px', lineHeight: '1.6',
        whiteSpace: isMarkdown ? 'normal' : 'pre-wrap',
        color: 'var(--obsidian-text-primary)'
      }}>
        {isMarkdown ? (
          <div dangerouslySetInnerHTML={{ __html: renderBasicMarkdown(preview.content) }} />
        ) : (
          <pre style={{ margin: 0, fontFamily: 'monospace', tabSize: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {preview.content}
          </pre>
        )}
      </div>
    );
  };

  // Basic markdown renderer (headers, bold, italic, lists, code blocks)
  const renderBasicMarkdown = (md: string): string => {
    return md
      // Code blocks (must be before inline code)
      .replace(/```[\s\S]*?```/g, match => {
        const code = match.slice(3, -3).replace(/^\w+\n/, '');
        return `<pre style="background:var(--obsidian-bg-secondary);padding:12px;border-radius:6px;overflow-x:auto;font-size:13px"><code>${escapeHtml(code)}</code></pre>`;
      })
      // Inline code
      .replace(/`([^`]+)`/g, '<code style="background:var(--obsidian-bg-secondary);padding:2px 6px;border-radius:3px;font-size:13px">$1</code>')
      // Headers
      .replace(/^#### (.+)$/gm, '<h4 style="margin:16px 0 8px;color:var(--obsidian-text-primary)">$1</h4>')
      .replace(/^### (.+)$/gm, '<h3 style="margin:20px 0 8px;color:var(--obsidian-text-primary)">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 style="margin:24px 0 8px;color:var(--obsidian-text-primary)">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 style="margin:24px 0 12px;color:var(--obsidian-text-primary)">$1</h1>')
      // Bold and italic
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Checkboxes
      .replace(/^- \[x\] (.+)$/gm, '<div style="margin:4px 0;display:flex;align-items:center;gap:6px"><span style="color:var(--obsidian-accent)">&#9745;</span><span style="text-decoration:line-through;opacity:0.7">$1</span></div>')
      .replace(/^- \[ \] (.+)$/gm, '<div style="margin:4px 0;display:flex;align-items:center;gap:6px"><span style="opacity:0.5">&#9744;</span><span>$1</span></div>')
      // Unordered lists
      .replace(/^- (.+)$/gm, '<div style="margin:4px 0;padding-left:16px">&#8226; $1</div>')
      // Horizontal rules
      .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid var(--obsidian-border);margin:16px 0">')
      // Paragraphs (double newlines)
      .replace(/\n\n/g, '<br><br>')
      // Single newlines
      .replace(/\n/g, '<br>');
  };

  const escapeHtml = (str: string): string => {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  };

  // Render image preview with zoom controls
  const renderImagePreview = () => {
    // For SharePoint images, we need the webUrl or a download URL
    const imageUrl = file.webUrl;

    return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'var(--obsidian-bg-secondary)', overflow: 'hidden', position: 'relative'
      }}>
        {imageUrl ? (
          <>
            <div style={{
              position: 'absolute', top: '12px', right: '12px', zIndex: 2,
              display: 'flex', gap: '4px', background: 'rgba(0,0,0,0.6)',
              borderRadius: '6px', padding: '4px'
            }}>
              <button onClick={() => setImageZoom(z => Math.max(0.25, z - 0.25))}
                style={zoomBtnStyle} title="Zoom out">
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>zoom_out</span>
              </button>
              <span style={{ color: 'white', fontSize: '12px', padding: '4px 8px', display: 'flex', alignItems: 'center' }}>
                {Math.round(imageZoom * 100)}%
              </span>
              <button onClick={() => setImageZoom(z => Math.min(4, z + 0.25))}
                style={zoomBtnStyle} title="Zoom in">
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>zoom_in</span>
              </button>
              <button onClick={() => setImageZoom(1)}
                style={zoomBtnStyle} title="Reset zoom">
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>fit_screen</span>
              </button>
            </div>
            <img
              src={imageUrl}
              alt={file.name}
              style={{
                maxWidth: `${100 * imageZoom}%`,
                maxHeight: `${100 * imageZoom}%`,
                objectFit: 'contain',
                transition: 'transform 0.2s ease',
                borderRadius: '4px'
              }}
              onError={() => setError('Image could not be loaded. Try opening in SharePoint.')}
            />
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '64px', color: getFileColor(), opacity: 0.6 }}>image</span>
            <p style={{ color: 'var(--obsidian-text-secondary)', marginTop: '12px' }}>
              Image preview requires SharePoint access
            </p>
            {file.webUrl && (
              <button onClick={handleOpenInSharePoint} style={openExternalBtnStyle}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>open_in_new</span>
                View in SharePoint
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render office document placeholder with open-in-SharePoint
  const renderOfficePreview = () => (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'var(--obsidian-bg)', padding: '40px'
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: '72px', color: getFileColor(), marginBottom: '16px' }}>
        {getFileIcon()}
      </span>
      <h3 style={{ margin: '0 0 8px', color: 'var(--obsidian-text-primary)', fontSize: '18px' }}>
        {file.name}
      </h3>
      <p style={{ color: 'var(--obsidian-text-secondary)', margin: '0 0 24px', fontSize: '14px' }}>
        {extension.toUpperCase()} Document &middot; {formatFileSize(file.size)}
      </p>
      {file.webUrl ? (
        <button onClick={handleOpenInSharePoint} style={openExternalBtnStyle}>
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>open_in_new</span>
          Open in SharePoint
        </button>
      ) : (
        <p style={{ color: 'var(--obsidian-text-secondary)', fontSize: '13px' }}>
          No preview URL available
        </p>
      )}
    </div>
  );

  // Render PDF placeholder
  const renderPdfPreview = () => (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'var(--obsidian-bg)', padding: '40px'
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: '72px', color: '#e53935', marginBottom: '16px' }}>
        picture_as_pdf
      </span>
      <h3 style={{ margin: '0 0 8px', color: 'var(--obsidian-text-primary)', fontSize: '18px' }}>
        {file.name}
      </h3>
      <p style={{ color: 'var(--obsidian-text-secondary)', margin: '0 0 24px', fontSize: '14px' }}>
        PDF Document &middot; {formatFileSize(file.size)}
      </p>
      {file.webUrl ? (
        <button onClick={handleOpenInSharePoint} style={openExternalBtnStyle}>
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>open_in_new</span>
          Open in SharePoint
        </button>
      ) : (
        <p style={{ color: 'var(--obsidian-text-secondary)', fontSize: '13px' }}>
          No preview URL available
        </p>
      )}
    </div>
  );

  // Determine what to render for the preview area
  const renderPreviewContent = () => {
    if (loading) {
      return (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          color: 'var(--obsidian-text-secondary)'
        }}>
          <div className="loading-dots" style={{ marginBottom: '12px' }}>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
          <p>Loading preview...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', padding: '40px'
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: '48px', color: 'var(--obsidian-text-secondary)', marginBottom: '12px' }}>
            error_outline
          </span>
          <p style={{ color: 'var(--obsidian-text-secondary)', marginBottom: '16px' }}>{error}</p>
          {file.webUrl && (
            <button onClick={handleOpenInSharePoint} style={openExternalBtnStyle}>
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>open_in_new</span>
              Open in SharePoint
            </button>
          )}
        </div>
      );
    }

    // Text/markdown with inline content
    if (preview?.previewType === 'text' && preview.content) {
      return renderTextContent();
    }

    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) {
      return renderImagePreview();
    }

    // Office documents
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) {
      return renderOfficePreview();
    }

    // PDF files
    if (extension === 'pdf') {
      return renderPdfPreview();
    }

    // Non-SharePoint files - use legacy behavior
    if (!isSharePoint) {
      if (file.url) {
        return (
          <div style={{ flex: 1 }}>
            <iframe
              src={file.url}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={file.name || 'File Preview'}
            />
          </div>
        );
      }
    }

    // Fallback
    return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: '40px'
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: '64px', color: getFileColor(), opacity: 0.5 }}>
          {getFileIcon()}
        </span>
        <p style={{ color: 'var(--obsidian-text-secondary)', marginTop: '16px' }}>
          Preview not available for this file type
        </p>
        {file.webUrl && (
          <button onClick={handleOpenInSharePoint} style={{ ...openExternalBtnStyle, marginTop: '16px' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>open_in_new</span>
            Open in SharePoint
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      backgroundColor: 'var(--obsidian-bg)'
    }}>
      {/* File header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '12px 20px',
        borderBottom: '1px solid var(--obsidian-border)',
        backgroundColor: 'var(--obsidian-bg-secondary)',
        flexShrink: 0
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: '24px', color: getFileColor() }}>
          {getFileIcon()}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '15px', fontWeight: 600,
            color: 'var(--obsidian-text-primary)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
          }}>
            {file.name || 'File'}
          </div>
          <div style={{
            fontSize: '12px', color: 'var(--obsidian-text-secondary)',
            display: 'flex', gap: '12px', marginTop: '2px'
          }}>
            {extension && <span>{extension.toUpperCase()}</span>}
            {file.size && <span>{formatFileSize(file.size)}</span>}
            {file.lastModified && <span>{formatDate(file.lastModified)}</span>}
            {isSharePoint && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>cloud</span>
                SharePoint
              </span>
            )}
          </div>
        </div>
        {file.webUrl && (
          <button
            onClick={handleOpenInSharePoint}
            style={{
              background: 'none', border: '1px solid var(--obsidian-border)',
              borderRadius: '6px', padding: '6px 12px',
              color: 'var(--obsidian-text-primary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '13px', whiteSpace: 'nowrap',
              transition: 'background-color 0.15s'
            }}
            title="Open in SharePoint"
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--obsidian-hover-bg)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>open_in_new</span>
            Open
          </button>
        )}
      </div>

      {/* Preview content area */}
      {renderPreviewContent()}
    </div>
  );
};

// Shared button styles
const openExternalBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '8px',
  padding: '10px 20px', border: 'none', borderRadius: '8px',
  backgroundColor: 'var(--obsidian-accent)', color: 'white',
  cursor: 'pointer', fontSize: '14px', fontWeight: 500,
  transition: 'opacity 0.15s'
};

const zoomBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: 'white',
  cursor: 'pointer', padding: '4px', display: 'flex',
  alignItems: 'center', borderRadius: '4px'
};

export default FileDetails;
